/**
 * Поиск по площадке (F1/F3, дизайн B7): клиентский, оффлайн-first.
 * Индекс: units + pois (+ живой слой occupants) — имена всех языков,
 * search_aliases, offer_tags; «товар → точки» — через словарь тегов
 * (BASE_TAGS: names+synonyms) и товарный слой products[] (ритейл: товар
 * ведёт к полке, код места «R155» — точный хит). Подпись тег-результатов —
 * «обычно есть здесь», НИКОГДА «в наличии» (ADR 0014).
 */
import type { AssortmentTag, PromoTier, Venue } from '@app/rvf'
import { BASE_TAGS, shelfCodeOf } from '@app/rvf'
import type { LiveOccupant } from './occupants'
import { cyrillicToLatin, normText, queryVariants, scoreKeyword } from './search-text'

/** Вид результата (табы палитры): арендатор / здание / инфраструктура / товар. */
export type SearchKind = 'tenant' | 'building' | 'infra' | 'product'

export interface SearchEntry {
  readonly id: string
  /** Уникальный ключ строки: id, у товаров `{poiId}::{productId}`. */
  readonly key: string
  readonly kind: SearchKind
  /** Категория RVF / тип узла — иконка и подпись строки. */
  readonly category: string
  /** Этаж арендатора (attrs.floor), если есть. */
  readonly floor: number | null
  /** Сырые часы работы («9–18», «24/7») для «Открыто сейчас». */
  readonly hours: string
  /** Нормализованные строки имени (все языки + арендатор). */
  readonly names: readonly string[]
  /** Нормализованные алиасы, теги и синонимы категории. */
  readonly aliases: readonly string[]
  /** Слаги тегов точки (offer_tags живого слоя + совпавшие по словарю). */
  readonly tagSlugs: readonly string[]
  /** Товар, ведущий к этой полке (kind='product'). */
  readonly product?: { readonly id: string }
}

export interface SearchHit {
  readonly id: string
  /** Уникальный ключ строки (у товаров одной полки id совпадает, key — нет). */
  readonly key: string
  readonly kind: SearchKind
  readonly category: string
  readonly floor: number | null
  readonly hours: string
  /** Найдено через тег ассортимента → подпись «обычно есть здесь». */
  readonly viaTag: string | null
  readonly score: number
  /** Товар в строке (kind='product'): цель маршрута — его полка (id). */
  readonly product?: { readonly id: string }
  /** Оплаченное продвижение (ADR 0019) — строка помечается словом «Реклама». */
  readonly promo?: PromoTier
}

/** Нормализация: нижний регистр, ё→е, трим; диакритика ET сохраняется. */
export function norm(value: string): string {
  return normText(value)
}

/** Синонимы категорий/типов: «фура» найдёт рампу, «поесть» — кафе. */
const CATEGORY_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  shop: ['магазин', 'купить', 'shop', 'store', 'pood'],
  warehouse: ['склад', 'логистика', 'хранение', 'отгрузка', 'warehouse', 'ladu'],
  office: ['офис', 'компания', 'office', 'kontor'],
  cafe: ['кафе', 'еда', 'кофе', 'поесть', 'обед', 'food', 'coffee', 'kohvik', 'söök'],
  auto: ['авто', 'сервис', 'ремонт', 'шиномонтаж', 'car', 'auto'],
  factory: ['производство', 'цех', 'мастерская', 'tootmine'],
  parking: ['парковка', 'стоянка', 'машина', 'parking', 'parkla'],
  checkpoint: ['кпп', 'шлагбаум', 'въезд', 'ворота', 'checkpoint', 'пропуск'],
  dock: ['рампа', 'док', 'погрузка', 'разгрузка', 'фура', 'dock', 'ramp'],
  loading_dock: ['рампа', 'док', 'погрузка', 'разгрузка', 'фура', 'dock', 'ramp'],
  entrance: ['вход', 'дверь', 'выход', 'entrance', 'door', 'uks'],
  door: ['вход', 'дверь', 'выход', 'entrance', 'door', 'uks'],
  elevator: ['лифт', 'elevator', 'lift'],
  stairs: ['лестница', 'ступени', 'stairs', 'trepp'],
  escalator: ['эскалатор', 'escalator'],
  qr: ['qr', 'куар', 'код', 'скан'],
  info: ['инфо', 'ресепшн', 'стойка', 'info', 'reception'],
  wc: ['туалет', 'санузел', 'wc', 'tualett'],
  truck_scale: ['весовая', 'весы', 'scale', 'kaal'],
  building: ['здание', 'корпус', 'building', 'hoone'],
}

const TENANT_CATEGORIES = new Set(['shop', 'warehouse', 'office', 'cafe', 'auto', 'factory'])

function kindOfCategory(category: string): SearchKind {
  return TENANT_CATEGORIES.has(category) ? 'tenant' : 'infra'
}

function synonymsOf(category: string): readonly string[] {
  return CATEGORY_SYNONYMS[category] ?? []
}

/** Статус «открыто сейчас» по строке часов; null — часов нет/не поняли. */
export function isOpenNow(hours: string, now = new Date()): boolean | null {
  if (!hours) return null
  const t = hours.toLowerCase()
  if (t.includes('24/7') || t.includes('круглосуточ')) return true
  const m = hours.match(/(\d{1,2})[:.]?(\d{2})?\s*[-–—]\s*(\d{1,2})[:.]?(\d{2})?/)
  if (!m) return null
  const open = Number(m[1]) * 60 + Number(m[2] ?? 0)
  const close = Number(m[3]) * 60 + Number(m[4] ?? 0)
  const nowMin = now.getHours() * 60 + now.getMinutes()
  return close <= open ? nowMin >= open || nowMin < close : nowMin >= open && nowMin < close
}

function localizedValues(text: Readonly<Record<string, string>> | undefined): string[] {
  return Object.values(text ?? {}).map(norm)
}

/** Транслит-дубликаты имён: латинский запрос находит кириллицу и наоборот. */
function withTranslit(values: readonly string[]): string[] {
  const out = new Set<string>()
  for (const v of values) {
    if (!v) continue
    out.add(v)
    const lat = cyrillicToLatin(v)
    if (lat !== v) out.add(lat)
  }
  return [...out]
}

/** Индекс площадки; live-слой обогащает арендаторами и их тегами. */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: три однотипных прохода (units/pois/nodes) — единый индекс
export function buildSearchIndex(
  venue: Venue,
  live: ReadonlyMap<string, LiveOccupant> | null,
): SearchEntry[] {
  const entries: SearchEntry[] = []
  for (const unit of venue.units) {
    const occupant = live?.get(unit.id)
    // Живая карточка сильнее карты и здесь (live-card.ts): по категории,
    // выбранной арендатором, ищут так же, как по имени.
    const category = occupant?.category?.trim() || unit.category
    // Часы у зданий не индексировались вовсе — фильтр «открыто сейчас» для них
    // молча не работал, хотя кабинет обещает арендатору «находят по часам».
    const hours = occupant?.hours || String(unit.attrs?.['hours'] ?? '')
    entries.push({
      id: unit.id,
      key: unit.id,
      kind: 'building',
      category,
      floor: null,
      hours,
      names: withTranslit([
        ...localizedValues(unit.name),
        ...(occupant?.name ? localizedValues(occupant.name) : []),
        ...(unit.occupants ?? []).map((o) => norm(o.name)),
      ]),
      aliases: [
        ...(unit.search_aliases ?? []).map(norm),
        ...synonymsOf('building'),
        ...synonymsOf(category),
      ],
      tagSlugs: (occupant?.offer_tags ?? []).map(norm),
    })
  }
  for (const poi of venue.pois) {
    // Код места («R155») — точное имя: продавец диктует, покупатель вводит.
    const code = shelfCodeOf(poi)
    entries.push({
      id: poi.id,
      key: poi.id,
      kind: kindOfCategory(poi.category),
      category: poi.category,
      floor: typeof poi.attrs?.['floor'] === 'number' ? (poi.attrs['floor'] as number) : null,
      hours: String(poi.attrs?.['hours'] ?? ''),
      names: withTranslit([...localizedValues(poi.name), ...(code ? [norm(code)] : [])]),
      aliases: [
        ...(poi.search_aliases ?? []).map(norm),
        norm(poi.category),
        ...synonymsOf(poi.category),
      ],
      tagSlugs: [],
    })
  }
  // Товары (ритейл): строка на товар, цель — его полка. Имена — сильные
  // совпадения, свободные теги — приглушённые алиасы (ниже точного имени).
  const poiById = new Map(venue.pois.map((p) => [p.id, p]))
  for (const product of venue.products ?? []) {
    const shelf = poiById.get(product.shelf)
    if (!shelf) continue
    entries.push({
      id: shelf.id,
      key: `${shelf.id}::${product.id}`,
      kind: 'product',
      category: shelf.category,
      floor: typeof shelf.attrs?.['floor'] === 'number' ? (shelf.attrs['floor'] as number) : null,
      hours: '',
      names: withTranslit(localizedValues(product.name)),
      aliases: (product.tags ?? []).map(norm),
      tagSlugs: [],
      product: { id: product.id },
    })
  }
  // Двери и рампы без POI — тоже цели («вход», «рампа» находят их).
  const takenPois = new Set(venue.network.nodes.map((n) => n.poi).filter(Boolean))
  for (const node of venue.network.nodes) {
    if (node.poi || takenPois.has(node.id)) continue
    if (node.kind !== 'door' && node.kind !== 'entrance' && node.kind !== 'dock') continue
    entries.push({
      id: node.id,
      key: node.id,
      kind: 'infra',
      category: node.kind === 'dock' ? 'dock' : 'entrance',
      floor: null,
      hours: '',
      names: withTranslit(localizedValues(node.name as Record<string, string> | undefined)),
      aliases: [...synonymsOf(node.kind === 'dock' ? 'dock' : 'entrance')],
      tagSlugs: [],
    })
  }
  return entries
}

/** Слаги тегов словаря, матчащих запрос (имя или синоним). */
export function matchTags(query: string, tags: readonly AssortmentTag[] = BASE_TAGS): string[] {
  const q = norm(query)
  if (q.length < 2) return []
  const hits: string[] = []
  for (const tag of tags) {
    const inNames = Object.values(tag.names).some((n) => norm(n).includes(q))
    const inSynonyms = tag.synonyms.some((s) => s.includes(q) || q.includes(s))
    if (inNames || inSynonyms || tag.slug.includes(q)) hits.push(tag.slug)
  }
  return hits
}

const SCORE_TAG = 20
const ALIAS_DAMP = 0.6

/** Скоринг записи: имена (fuzzy, все варианты запроса) → алиасы → теги. */
function scoreEntry(
  entry: SearchEntry,
  variants: readonly string[],
  tagSlugs: ReadonlySet<string>,
): SearchHit | null {
  let score = 0
  for (const needle of variants) {
    for (const name of entry.names) score = Math.max(score, scoreKeyword(needle, name))
    for (const alias of entry.aliases) {
      score = Math.max(score, scoreKeyword(needle, alias) * ALIAS_DAMP)
    }
  }
  if (score > 0) {
    return {
      id: entry.id,
      key: entry.key,
      kind: entry.kind,
      category: entry.category,
      floor: entry.floor,
      hours: entry.hours,
      viaTag: null,
      score,
      ...(entry.product ? { product: entry.product } : {}),
    }
  }
  const q = variants[0] ?? ''
  const matched = entry.tagSlugs.find((slug) => tagSlugs.has(slug) || slug.includes(q))
  return matched
    ? {
        id: entry.id,
        key: entry.key,
        kind: entry.kind,
        category: entry.category,
        floor: entry.floor,
        hours: entry.hours,
        viaTag: matched,
        score: SCORE_TAG,
        ...(entry.product ? { product: entry.product } : {}),
      }
    : null
}

export interface SearchFilter {
  kind?: SearchKind | 'all'
  openNow?: boolean
}

/** Поиск: опечатки/транслит/раскладка, табы видов, «Открыто сейчас». */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: один проход по индексу с фильтрами
export function searchVenue(
  index: readonly SearchEntry[],
  query: string,
  tags: readonly AssortmentTag[] = BASE_TAGS,
  filter: SearchFilter = {},
): SearchHit[] {
  const q = norm(query)
  if (q.length < 2) return []
  const variants = queryVariants(query)
  const tagSlugs = new Set(matchTags(q, tags))
  const hits: SearchHit[] = []
  for (const entry of index) {
    if (filter.kind && filter.kind !== 'all' && entry.kind !== filter.kind) continue
    if (filter.openNow && entry.kind === 'tenant' && isOpenNow(entry.hours) === false) continue
    const hit = scoreEntry(entry, variants, tagSlugs)
    // Порог: слабые подпоследовательности через синонимы — шум, не результат.
    if (hit && hit.score >= 28) {
      hits.push({ ...hit, score: hit.score + (hit.kind === 'tenant' ? 6 : 0) })
    } else if (hit?.viaTag) {
      hits.push(hit)
    }
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, 30)
}

/**
 * Порог «сильного» совпадения: точное или почти точное попадание по имени.
 * Оплаченная строка встаёт ПОСЛЕ всего, что набрало столько же и больше.
 */
export const STRONG_HIT = 84

/**
 * Оплаченное продвижение в выдаче (ADR 0019, PROMO §6, docs/ui/viewer/10-promo).
 *
 * ТРИ ПРАВИЛА, И НИ ОДНО ИЗ НИХ НЕ ПРО ДЕНЬГИ:
 *
 *  1. НИЧЕГО НЕ ДОПИСЫВАЕТСЯ. Продвижение работает только среди того, что и
 *     так нашлось по запросу. Показать оплаченную точку, не подходящую под
 *     запрос, — это не реклама, а неверный ответ на вопрос «где это»;
 *     первая версия так и делала, и это было ошибкой.
 *  2. ТОЧНОЕ СОВПАДЕНИЕ НЕПОБЕДИМО. Строка с именем-совпадением (score ≥ 84)
 *     не может быть обойдена оплатой ни при каких условиях.
 *  3. ПОДНИМАЕТСЯ ОДНА СТРОКА. Среди слабых совпадений оплаченная поднимается
 *     на первое место после блока сильных — и получает пометку «Реклама».
 *     Остальные оплаченные остаются на своих местах, тоже помеченные.
 */
export function withPromoRow(
  hits: readonly SearchHit[],
  _index: readonly SearchEntry[],
  promos: ReadonlyMap<string, PromoTier>,
): SearchHit[] {
  if (promos.size === 0 || hits.length === 0) return [...hits]
  const marked = hits.map((hit) => {
    // Ключ строки точнее id: у товарной строки продвигается товар, не полка.
    const tier = promos.get(hit.key) ?? promos.get(hit.id)
    return tier ? { ...hit, promo: tier } : hit
  })
  // Граница неприкосновенного: всё, что набрало ≥ 84, остаётся как есть.
  const strong = marked.filter((h) => h.score >= STRONG_HIT).length
  const weak = marked.slice(strong)
  const liftIndex = weak.findIndex((h) => h.promo !== undefined)
  // Поднимать нечего или оно и так первое среди слабых — ничего не двигаем.
  if (liftIndex <= 0) return marked
  const lifted = weak[liftIndex] as SearchHit
  return [...marked.slice(0, strong), lifted, ...weak.filter((_, i) => i !== liftIndex)]
}

/**
 * Промо-уровни товарных строк (ADR 0019): ключ `{poiId}::{productId}` → tier.
 * Окно активности проверяется здесь (нет окна — активен: статика площадки);
 * ФАЗУ экрана (реклама гаснет на маршруте) проверяет вызывающий — promo.ts.
 */
export function productPromoTiers(venue: Venue, now = Date.now()): Map<string, PromoTier> {
  const out = new Map<string, PromoTier>()
  for (const product of venue.products ?? []) {
    const emphasis = product.emphasis
    if (!emphasis || (emphasis.tier !== 'promo' && emphasis.tier !== 'featured')) continue
    if (emphasis.active) {
      const from = Date.parse(emphasis.active.from)
      const to = Date.parse(emphasis.active.to)
      if (!Number.isFinite(from) || !Number.isFinite(to) || now < from || now >= to) continue
    }
    out.set(`${product.shelf}::${product.id}`, emphasis.tier)
  }
  return out
}

const RECENT_KEY = 'search-recent'
const RECENT_MAX = 5

/** «Недавнее»: только выбранные цели (id), на устройстве. */
export function loadRecent(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

export function pushRecent(id: string): void {
  const next = [id, ...loadRecent().filter((x) => x !== id)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // квота/приватный режим — «недавнее» просто не сохранится
  }
}
